import { detectCountryFromPhone } from '@/lib/countries';
const PHONE_STRIP_RE = /[^\d+]/g;
const PHONE_MIN_DIGITS = 8;

export function stripNonPhoneChars(value: string): string {
  return value.replace(PHONE_STRIP_RE, '');
}

/**
 * The national part of a number: "+228 90 12 34 56" gives "90123456". The
 * sign-up field always starts with the dialling code, which must not count
 * towards the national length (it made every Togolese number look too long).
 */
export function nationalDigits(value: string): string {
  const compact = stripNonPhoneChars(value);
  const country = detectCountryFromPhone(compact);
  const national = country ? compact.slice(country.dial.length) : compact;
  return national.replace(/\D/g, '');
}

export function isValidPhone(value: string, digits?: number): boolean {
  const national = nationalDigits(value);
  return digits ? national.length === digits : national.length >= PHONE_MIN_DIGITS;
}

export function getPhoneError(value: string, lang: 'fr' | 'en', digits?: number): string | null {
  // Nothing typed yet (only the prefilled dialling code): the field is optional.
  if (!value || nationalDigits(value).length === 0) return null;
  if (!isValidPhone(value, digits)) {
    const expected = digits ?? PHONE_MIN_DIGITS;
    return lang === 'fr'
      ? `Minimum ${expected} chiffres requis`
      : `Minimum ${expected} digits required`;
  }
  return null;
}

export const PASSWORD_MIN_LENGTH = 8;

export interface PasswordCriteria {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasDigit: boolean;
  hasSpecial: boolean;
}

export function getPasswordCriteria(value: string): PasswordCriteria {
  return {
    minLength: value.length >= PASSWORD_MIN_LENGTH,
    hasUppercase: /[A-Z]/.test(value),
    hasLowercase: /[a-z]/.test(value),
    hasDigit: /\d/.test(value),
    hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(value),
  };
}

export function getPasswordStrength(value: string): {
  valid: boolean;
  tooShort: boolean;
  score: number;
  criteria: PasswordCriteria;
} {
  const criteria = getPasswordCriteria(value);
  const score = Object.values(criteria).filter(Boolean).length;
  return {
    valid: score === 5,
    tooShort: value.length > 0 && value.length < PASSWORD_MIN_LENGTH,
    score,
    criteria,
  };
}

export function passwordsMatch(a: string, b: string): boolean {
  return a.length > 0 && a === b;
}
